import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Chip, IconButton,
  Drawer, Divider, TextField, Rating, CircularProgress,
  Alert, LinearProgress, InputAdornment,
} from '@mui/material';
import {
  ShoppingCart, Add, Remove, Close, Check, Send,
  Restaurant as RestaurantIcon, Star, Person, Phone,
  Fastfood, CreditCard, AccountBalance, Money, ArrowBack,
} from '@mui/icons-material';
import axios from 'axios';
import { food, gradients, shadows } from '../theme';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5134/api';
const IMAGE_BASE = API_BASE.replace('/api', '');

// ── Types ─────────────────────────────────────────────────────────────────────

interface MenuItem {
  id: string;
  name: string;
  price: number;
  description?: string;
  isAvailable: boolean;
  tags?: string[];
}

interface CartItem { item: MenuItem; qty: number; note: string }

interface Gateway {
  id: string;
  name: string;
  displayName: string;
  isTestMode: boolean;
  supportsCard: boolean;
  supportsUPI: boolean;
  supportsWallet: boolean;
  supportsNetBanking: boolean;
  publicKey?: string;
}

interface PaymentOption {
  id: string;
  label: string;
  icon: React.ReactNode;
  type: 'cash' | 'upi' | 'card' | 'wallet' | 'netbanking' | 'counter';
  gateway?: Gateway;
  color: string;
  emoji: string;
}

type PageStep = 'menu' | 'payment' | 'confirmed';

const STATUS_STEPS = [
  { key: 'Pending',   label: 'Received',  icon: '📋' },
  { key: 'Confirmed', label: 'Confirmed', icon: '✅' },
  { key: 'Preparing', label: 'Cooking',   icon: '👨‍🍳' },
  { key: 'Ready',     label: 'Ready',     icon: '🍽️' },
  { key: 'Served',    label: 'Served',    icon: '🎉' },
];

const STATUS_PROGRESS: Record<string, number> = {
  Pending: 10, Confirmed: 30, Preparing: 55, Ready: 80, Served: 100, Completed: 100,
};

declare global { interface Window { Razorpay: any } }

// Shell lives OUTSIDE so its identity is stable — prevents TextField focus loss on re-render
const Shell: React.FC<{ children: React.ReactNode; showHeader?: boolean; tableInfo?: any }> = ({
  children, showHeader = true, tableInfo,
}) => (
  <Box sx={{ minHeight: '100vh', background: food.bg, backgroundImage: gradients.mesh.light, maxWidth: 480, mx: 'auto' }}>
    {showHeader && tableInfo && (
      <Box sx={{ background: food.red, px: 2, py: 1.5, textAlign: 'center', position: 'sticky', top: 0, zIndex: 100, boxShadow: '0 4px 20px rgba(224,42,42,0.3)' }}>
        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.2 }}>{tableInfo.restaurantName}</Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>Table {tableInfo.tableNumber}</Typography>
      </Box>
    )}
    {children}
  </Box>
);

const CustomerOrderPage: React.FC = () => {
  const tableId = window.location.pathname.split('/order/')[1]?.split('?')[0];

  const [step, setStep]               = useState<PageStep>('menu');
  const [tableInfo, setTableInfo]     = useState<any>(null);
  const [menu, setMenu]               = useState<any[]>([]);
  const [gateways, setGateways]       = useState<Gateway[]>([]);
  const [cart, setCart]               = useState<CartItem[]>([]);
  const [cartOpen, setCartOpen]       = useState(false);
  const [loading, setLoading]         = useState(true);
  const [loadError, setLoadError]     = useState('');
  const [placing, setPlacing]         = useState(false);
  const [paymentBusy, setPaymentBusy] = useState(false);
  const [orderResult, setOrderResult] = useState<any>(null);
  const [orderStatus, setOrderStatus] = useState<any>(null);
  const [activeCategory, setActiveCategory] = useState('');
  const [orderNotes, setOrderNotes]   = useState('');
  const [customerName, setCustomerName]   = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [nameError, setNameError]     = useState('');
  const [phoneError, setPhoneError]   = useState('');
  const [selectedPaymentOption, setSelectedPaymentOption] = useState<PaymentOption | null>(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [feedback, setFeedback]       = useState({ rating: 0, foodRating: 0, serviceRating: 0, comment: '' });
  const [chatOpen, setChatOpen]       = useState(false);
  const [chatMessages, setChatMessages] = useState<{sender: 'user'|'bot', text: string, items?: any[]}[]>([]);
  const [chatInput, setChatInput]     = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const glassCard = {
    background: food.glass,
    backdropFilter: 'blur(24px)',
    WebkitBackdropFilter: 'blur(24px)',
    border: `1px solid ${food.glassBorder}`,
    borderRadius: '20px',
    boxShadow: shadows.glass,
  };
  const pillBtn = {
    borderRadius: '9999px',
    fontWeight: 800,
    textTransform: 'none' as const,
    transition: 'all 0.2s ease',
    '&:hover': { transform: 'translateY(-1px)' },
  };

  // ── Build payment options ─────────────────────────────────────────────────
  const paymentOptions: PaymentOption[] = (() => {
    const opts: PaymentOption[] = [];
    for (const gw of gateways) {
      if (gw.name === 'Razorpay') {
        if (gw.supportsUPI)        opts.push({ id: `${gw.id}-upi`,    label: 'UPI / QR Pay',        emoji: '📱', icon: <span>📱</span>, type: 'upi',        gateway: gw, color: '#7c3aed' });
        if (gw.supportsCard)       opts.push({ id: `${gw.id}-card`,   label: 'Credit / Debit Card',  emoji: '💳', icon: <CreditCard />,  type: 'card',       gateway: gw, color: '#2563eb' });
        if (gw.supportsWallet)     opts.push({ id: `${gw.id}-wallet`, label: 'Wallet',               emoji: '👛', icon: <span>👛</span>, type: 'wallet',     gateway: gw, color: '#db2777' });
        if (gw.supportsNetBanking) opts.push({ id: `${gw.id}-nb`,    label: 'Net Banking',           emoji: '🏦', icon: <AccountBalance />, type: 'netbanking', gateway: gw, color: '#d97706' });
      }
      if ((gw.name === 'Stripe' || gw.name === 'PayPal') && gw.supportsCard)
        opts.push({ id: `${gw.id}-card`, label: `Card (${gw.displayName})`, emoji: '💳', icon: <CreditCard />, type: 'card', gateway: gw, color: '#635bff' });
    }
    opts.push({ id: 'counter', label: 'Pay at Counter', emoji: '💵', icon: <Money />, type: 'counter', color: '#16a34a' });
    return opts;
  })();

  // ── Load on mount ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!tableId) return;
    const init = async () => {
      setLoading(true);
      try {
        const tableRes = await axios.get(`${API_BASE}/public/table/${tableId}`);
        const tInfo = tableRes.data;
        setTableInfo(tInfo);
        const [menuRes, gwRes] = await Promise.all([
          axios.get(`${API_BASE}/public/menu/${tInfo.restaurantId}`),
          axios.get(`${API_BASE}/public/${tInfo.restaurantId}/payment-options`).catch(() => ({ data: [] })),
        ]);
        setMenu(menuRes.data.menu ?? []);
        setActiveCategory(menuRes.data.menu?.[0]?.category?.id ?? '');
        setGateways(gwRes.data ?? []);
      } catch (e: any) {
        if (e?.code === 'ERR_NETWORK' || e?.message?.includes('Network Error'))
          setLoadError('Cannot reach the server. Please ask staff for help.');
        else if (e?.response?.status === 404)
          setLoadError('Table not found. Please scan the QR code again.');
        else
          setLoadError('Something went wrong loading the menu. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [tableId]);

  useEffect(() => {
    // 1. Update Title
    if (tableInfo?.restaurantName) {
      document.title = `${tableInfo.restaurantName} - Self Ordering`;
    } else {
      document.title = 'Customer Self Ordering - MenuMaster';
    }

    // 2. Update Favicon
    const updateFavicon = (href: string) => {
      let link: HTMLLinkElement | null = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.getElementsByTagName('head')[0].appendChild(link);
      }
      link.href = href;
    };

    if (tableInfo?.restaurantLogo) {
      const logoUrl = tableInfo.restaurantLogo.startsWith('http') 
        ? tableInfo.restaurantLogo 
        : `${IMAGE_BASE}${tableInfo.restaurantLogo}`;
      updateFavicon(logoUrl);
    } else if (tableInfo) {
      updateFavicon(`data:image/svg+xml,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 100 100%22><text y=%22.9em%22 font-size=%2290%22>🍔</text></svg>`);
    } else {
      updateFavicon('/favicon.ico');
    }
  }, [tableInfo]);

  // ── Poll order status ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!orderResult?.orderId) return;
    const poll = async () => {
      try {
        const res = await axios.get(`${API_BASE}/public/order/${orderResult.orderId}/status`);
        setOrderStatus(res.data);
        if (res.data.status === 'Completed' || res.data.status === 'Cancelled')
          if (pollRef.current) clearInterval(pollRef.current);
      } catch { /* silent */ }
    };
    poll();
    pollRef.current = setInterval(poll, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [orderResult?.orderId]);

  // ── Cart helpers ──────────────────────────────────────────────────────────
  const addToCart = (item: MenuItem) =>
    setCart(prev => {
      const ex = prev.find(c => c.item.id === item.id);
      if (ex) return prev.map(c => c.item.id === item.id ? { ...c, qty: c.qty + 1 } : c);
      return [...prev, { item, qty: 1, note: '' }];
    });

  const removeFromCart = (id: string) =>
    setCart(prev => {
      const ex = prev.find(c => c.item.id === id);
      if (!ex) return prev;
      if (ex.qty === 1) return prev.filter(c => c.item.id !== id);
      return prev.map(c => c.item.id === id ? { ...c, qty: c.qty - 1 } : c);
    });

  const getQty    = (id: string) => cart.find(c => c.item.id === id)?.qty ?? 0;
  const cartTotal = cart.reduce((s, c) => s + c.item.price * c.qty, 0);
  const cartCount = cart.reduce((s, c) => s + c.qty, 0);

  // ── Validate details + place order ───────────────────────────────────────
  const validateDetails = (): boolean => {
    let ok = true;
    if (!customerName.trim()) { setNameError('Please enter your name'); ok = false; } else setNameError('');
    if (!customerPhone.trim()) { setPhoneError('Please enter your mobile number'); ok = false; }
    else if (!/^[6-9]\d{9}$/.test(customerPhone.trim())) { setPhoneError('Enter a valid 10-digit mobile number'); ok = false; }
    else setPhoneError('');
    return ok;
  };

  const placeOrder = async (): Promise<any> => {
    if (!validateDetails()) return null;
    if (!tableInfo || cart.length === 0) return null;
    setPlacing(true);
    try {
      const res = await axios.post(`${API_BASE}/public/order`, {
        restaurantId: tableInfo.restaurantId,
        tableId,
        notes: orderNotes,
        customerName: customerName.trim(),
        customerPhone: customerPhone.trim(),
        items: cart.map(c => ({ menuItemId: c.item.id, quantity: c.qty, specialInstructions: c.note || undefined })),
      });
      setOrderResult(res.data);
      setCart([]);
      setCartOpen(false);
      return res.data;
    } catch {
      alert('Failed to place order. Please try again.');
      return null;
    } finally {
      setPlacing(false);
    }
  };

  const handleSendChat = async () => {
      if (!chatInput.trim() || !tableInfo) return;
      const text = chatInput.trim();
      setChatInput('');
      setChatMessages(prev => [...prev, { sender: 'user', text }]);
      setChatLoading(true);
      try {
          const res = await axios.post(`${API_BASE}/public/chat`, { restaurantId: tableInfo.restaurantId, message: text });
          setChatMessages(prev => [...prev, { sender: 'bot', text: res.data.message, items: res.data.items }]);
      } catch (e) {
          setChatMessages(prev => [...prev, { sender: 'bot', text: 'Sorry, I am having trouble connecting to the kitchen right now.' }]);
      } finally {
          setChatLoading(false);
      }
  };

  const proceedToPayment = async () => {
    const result = await placeOrder();
    if (!result) return;
    setSelectedPaymentOption(paymentOptions[0] ?? null);
    setStep('payment');
  };

  // ── Razorpay ──────────────────────────────────────────────────────────────
  const loadRazorpayScript = (): Promise<boolean> =>
    new Promise(resolve => {
      if (window.Razorpay) { resolve(true); return; }
      const s = document.createElement('script');
      s.src = 'https://checkout.razorpay.com/v1/checkout.js';
      s.onload = () => resolve(true); s.onerror = () => resolve(false);
      document.body.appendChild(s);
    });

  const handleConfirmPayment = async () => {
    if (!selectedPaymentOption || !orderResult) return;
    const opt = selectedPaymentOption;

    if (opt.type === 'counter' || !opt.gateway || opt.gateway.name === 'Cash') {
      setPaymentBusy(true);
      try { await axios.post(`${API_BASE}/public/orders/${orderResult.orderId}/record-payment`, { paymentType: opt.type === 'counter' ? 'cash' : opt.type }); }
      catch { /* non-fatal */ }
      setPaymentBusy(false);
      setStep('confirmed');
      return;
    }

    if (opt.gateway.name === 'Razorpay') {
      setPaymentBusy(true);
      try {
        const loaded = await loadRazorpayScript();
        if (!loaded) { alert('Failed to load payment gateway. Please try again.'); setPaymentBusy(false); return; }
        const initRes = await axios.post(`${API_BASE}/public/orders/${orderResult.orderId}/initiate-razorpay`);
        const { razorpayOrderId, publicKey, amount } = initRes.data;
        await new Promise<void>((resolve, reject) => {
          const rzp = new window.Razorpay({
            key: publicKey, amount, currency: 'INR', order_id: razorpayOrderId,
            name: tableInfo.restaurantName,
            description: `Table ${tableInfo.tableNumber} · ${orderResult.orderNumber}`,
            prefill: { name: customerName, contact: customerPhone },
            theme: { color: '#E02A2A' },
            modal: { ondismiss: () => reject(new Error('dismissed')) },
            handler: async (response: any) => {
              try {
                await axios.post(`${API_BASE}/public/orders/${orderResult.orderId}/record-payment`, {
                  paymentType: 'razorpay', selectedMethod: opt.type,
                  razorpayOrderId: response.razorpay_order_id,
                  razorpayPaymentId: response.razorpay_payment_id,
                  razorpaySignature: response.razorpay_signature,
                });
                resolve();
              } catch (e) { reject(e); }
            },
          });
          rzp.open();
        });
        setStep('confirmed');
      } catch (e: any) {
        if (e?.message !== 'dismissed') alert('Payment failed. You can pay at the counter.');
        setStep('confirmed');
      } finally { setPaymentBusy(false); }
      return;
    }

    await axios.post(`${API_BASE}/public/orders/${orderResult.orderId}/record-payment`, { paymentType: 'cash' }).catch(() => {});
    setStep('confirmed');
  };

  const submitFeedback = async () => {
    if (!tableInfo || !feedback.rating) return;
    try {
      await axios.post(`${API_BASE}/public/feedback`, {
        restaurantId: tableInfo.restaurantId, orderId: orderResult?.orderId,
        overallRating: feedback.rating, foodRating: feedback.foodRating || null,
        serviceRating: feedback.serviceRating || null, comment: feedback.comment,
        customerName, customerPhone,
      });
      setFeedbackSent(true);
    } catch { alert('Failed to submit feedback'); }
  };

  // ── Loading / Error ───────────────────────────────────────────────────────
  if (loading && !tableInfo) {
    return (
      <Shell showHeader={false}>
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
          <RestaurantIcon sx={{ fontSize: 56, color: food.red }} />
          <CircularProgress sx={{ color: food.red }} />
          <Typography sx={{ color: food.inkMid, fontWeight: 500 }}>Loading menu…</Typography>
        </Box>
      </Shell>
    );
  }

  if (!tableInfo) {
    return (
      <Shell showHeader={false}>
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 3, gap: 2 }}>
          <Typography sx={{ fontSize: 48 }}>😕</Typography>
          <Alert severity="error" sx={{ borderRadius: '16px' }}>{loadError || 'Invalid QR code or table not found.'}</Alert>
          <Button variant="outlined" onClick={() => window.location.reload()}
            sx={{ borderRadius: '9999px', textTransform: 'none', color: food.red, borderColor: food.red }}>
            Try Again
          </Button>
        </Box>
      </Shell>
    );
  }

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // STEP: Payment selection
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  if (step === 'payment') {
    return (
      <Shell showHeader={false} tableInfo={tableInfo}>
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: food.bg }}>
          {/* Header */}
          <Box sx={{ background: food.red, px: 2, pt: 3, pb: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <IconButton onClick={() => setStep('menu')} size="small"
                sx={{ background: 'rgba(255,255,255,0.2)', color: '#fff', '&:hover': { background: 'rgba(255,255,255,0.3)' } }}>
                <ArrowBack fontSize="small" />
              </IconButton>
              <Box>
                <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1.1rem', lineHeight: 1.2 }}>Choose Payment</Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.75rem' }}>{tableInfo.restaurantName} · Table {tableInfo.tableNumber}</Typography>
              </Box>
            </Box>
            {/* Amount pill */}
            <Box sx={{ background: 'rgba(255,255,255,0.15)', borderRadius: '16px', p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.75rem' }}>{orderResult?.orderNumber}</Typography>
                <Typography sx={{ color: '#fff', fontWeight: 700 }}>{customerName}</Typography>
              </Box>
              <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: '1.6rem' }}>₹{Math.round(orderResult?.totalAmount ?? cartTotal)}</Typography>
            </Box>
          </Box>

          {/* Payment options — scrollable list */}
          <Box sx={{ flex: 1, overflowY: 'auto', px: 2, pt: 2, pb: 12 }}>
            <Typography sx={{ color: food.inkMid, fontSize: '0.78rem', fontWeight: 600, mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Select how to pay</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {paymentOptions.map(opt => {
                const isSelected = selectedPaymentOption?.id === opt.id;
                return (
                  <Box key={opt.id} onClick={() => setSelectedPaymentOption(opt)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5, p: '14px 16px',
                      borderRadius: '16px', cursor: 'pointer',
                      border: isSelected ? `2px solid ${opt.color}` : '2px solid transparent',
                      background: isSelected ? `${opt.color}12` : '#fff',
                      boxShadow: isSelected ? `0 2px 12px ${opt.color}25` : '0 1px 4px rgba(0,0,0,0.06)',
                      transition: 'all 0.15s ease',
                    }}>
                    {/* Icon circle */}
                    <Box sx={{ width: 46, height: 46, borderRadius: '12px', background: `${opt.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 22 }}>
                      {typeof opt.icon === 'string' ? opt.emoji : opt.emoji}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, color: food.inkDark, fontSize: '0.95rem', lineHeight: 1.2 }}>{opt.label}</Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: food.inkLight, mt: 0.25 }}>
                        {opt.type === 'counter' ? 'Pay with cash or UPI at counter' :
                         opt.gateway?.name === 'Razorpay' ? 'Secure · Instant' :
                         opt.gateway?.isTestMode ? 'Test Mode' : 'Online payment'}
                      </Typography>
                    </Box>
                    {/* Check indicator */}
                    <Box sx={{ width: 22, height: 22, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: isSelected ? opt.color : 'transparent',
                      border: isSelected ? 'none' : `2px solid ${food.glassBorder}`,
                      transition: 'all 0.15s' }}>
                      {isSelected && <Check sx={{ fontSize: 13, color: '#fff' }} />}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Sticky confirm button */}
          <Box sx={{ position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: 480, px: 2, pb: 3, pt: 1.5, background: 'linear-gradient(to top, #fff 70%, transparent)', zIndex: 100 }}>
            <Button variant="contained" fullWidth disabled={!selectedPaymentOption || paymentBusy}
              startIcon={paymentBusy ? <CircularProgress size={18} color="inherit" /> : undefined}
              onClick={handleConfirmPayment}
              sx={{ ...pillBtn, py: 1.75, fontSize: '1rem', background: selectedPaymentOption ? food.red : '#ccc', boxShadow: selectedPaymentOption ? '0 4px 16px rgba(224,42,42,0.35)' : 'none' }}>
              {paymentBusy ? 'Processing…' :
               selectedPaymentOption?.type === 'counter' ? 'Place Order — Pay at Counter' :
               `Pay ₹${Math.round(orderResult?.totalAmount ?? cartTotal)}`}
            </Button>
          </Box>
        </Box>
      </Shell>
    );
  }

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // STEP: Confirmed + live status
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  if (step === 'confirmed') {
    const currentStatus = orderStatus?.status ?? orderResult?.status ?? 'Pending';
    const progress      = STATUS_PROGRESS[currentStatus] ?? 10;
    const stepIdx       = STATUS_STEPS.findIndex(s => s.key === currentStatus);
    const isCancelled   = currentStatus === 'Cancelled';
    const isDone        = currentStatus === 'Completed' || currentStatus === 'Served';

    return (
      <Shell showHeader={false} tableInfo={tableInfo}>
        <Box sx={{ minHeight: '100vh', p: 2, pt: 4, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ width: 72, height: 72, borderRadius: '20px', background: isCancelled ? 'rgba(239,68,68,0.12)' : isDone ? 'rgba(34,197,94,0.12)' : 'rgba(224,42,42,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1.5 }}>
              <Typography sx={{ fontSize: 38 }}>{isCancelled ? '❌' : isDone ? '🎉' : '🍽️'}</Typography>
            </Box>
            <Typography variant="h5" sx={{ color: food.inkDark, fontWeight: 800, letterSpacing: '-0.02em' }}>
              {isCancelled ? 'Order Cancelled' : isDone ? 'Enjoy your meal!' : 'Order Confirmed!'}
            </Typography>
            <Typography sx={{ color: food.inkLight, mt: 0.5, fontSize: '0.875rem' }}>{tableInfo.restaurantName} · Table {tableInfo.tableNumber}</Typography>
          </Box>

          <Box sx={{ ...glassCard, p: 2.5, width: '100%' }}>
            {[
              ['Order Number', orderResult?.orderNumber],
              ['Customer', customerName],
              ['Total', `₹${Math.round(orderResult?.totalAmount ?? 0)}`],
              selectedPaymentOption ? ['Payment', selectedPaymentOption.label] : null,
            ].filter(Boolean).map(([label, value]: any) => (
              <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, '&:last-child': { mb: 0 } }}>
                <Typography sx={{ color: food.inkLight, fontSize: '0.8rem' }}>{label}</Typography>
                <Typography sx={{ color: label === 'Total' ? '#22c55e' : food.inkDark, fontWeight: label === 'Total' ? 800 : 600, fontSize: label === 'Total' ? '1rem' : '0.875rem' }}>{value}</Typography>
              </Box>
            ))}
          </Box>

          {!isCancelled && (
            <Box sx={{ ...glassCard, p: 2.5, width: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography sx={{ color: food.inkDark, fontWeight: 700 }}>Live Status</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: isDone ? '#22c55e' : food.red, animation: isDone ? 'none' : 'pulse 2s infinite' }} />
                  <Typography sx={{ color: food.inkLight, fontSize: '0.75rem' }}>Live</Typography>
                </Box>
              </Box>
              <LinearProgress variant="determinate" value={progress}
                sx={{ height: 8, borderRadius: 4, mb: 2, backgroundColor: 'rgba(224,42,42,0.1)',
                  '& .MuiLinearProgress-bar': { background: isDone ? 'linear-gradient(90deg,#22c55e,#16a34a)' : `linear-gradient(90deg,${food.red},#f97316)`, borderRadius: 4 } }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                {STATUS_STEPS.map((s, i) => {
                  const past = i < stepIdx; const current = i === stepIdx;
                  return (
                    <Box key={s.key} sx={{ textAlign: 'center', flex: 1 }}>
                      <Typography sx={{ fontSize: current ? 22 : 16, opacity: past || current ? 1 : 0.3 }}>{past ? '✅' : s.icon}</Typography>
                      <Typography sx={{ fontSize: '0.6rem', color: current ? food.red : food.inkLight, fontWeight: current ? 700 : 400, mt: 0.25, lineHeight: 1.2 }}>{s.label}</Typography>
                    </Box>
                  );
                })}
              </Box>
              <Box sx={{ mt: 2, p: 1.5, borderRadius: '12px', background: 'rgba(224,42,42,0.06)', textAlign: 'center' }}>
                <Typography sx={{ color: food.inkDark, fontSize: '0.875rem', fontWeight: 600 }}>
                  {orderStatus?.statusMessage ?? 'Order received — waiting for confirmation'}
                </Typography>
              </Box>
            </Box>
          )}

          {(isDone || currentStatus === 'Served') && !feedbackSent && (
            !feedbackOpen ? (
              <Button variant="outlined" startIcon={<Star />} onClick={() => setFeedbackOpen(true)}
                sx={{ ...pillBtn, color: food.red, borderColor: food.red }}>Rate your experience</Button>
            ) : (
              <Box sx={{ ...glassCard, p: 2.5, width: '100%' }}>
                <Typography sx={{ color: food.inkDark, fontWeight: 800, mb: 1.5 }}>How was your experience?</Typography>
                {[['Overall', 'rating'], ['Food', 'foodRating'], ['Service', 'serviceRating']].map(([lbl, key]) => (
                  <Box key={key} sx={{ mb: 1.5 }}>
                    <Typography variant="caption" sx={{ color: food.inkLight }}>{lbl}</Typography>
                    <Rating value={(feedback as any)[key]} onChange={(_, v) => setFeedback(f => ({ ...f, [key]: v ?? 0 }))} size={key === 'rating' ? 'large' : 'medium'} sx={{ display: 'block' }} />
                  </Box>
                ))}
                <TextField placeholder="Comments (optional)" multiline rows={2} fullWidth value={feedback.comment}
                  onChange={e => setFeedback(f => ({ ...f, comment: e.target.value }))}
                  sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: '14px' } }} />
                <Button variant="contained" fullWidth onClick={submitFeedback} disabled={!feedback.rating}
                  sx={{ ...pillBtn, background: food.red }}>Submit Feedback</Button>
              </Box>
            )
          )}
          {feedbackSent && <Alert severity="success" sx={{ borderRadius: '16px', width: '100%' }}>Thank you for your feedback! 🙏</Alert>}
          <Typography sx={{ color: food.inkLight, fontSize: '0.75rem', textAlign: 'center', pb: 2 }}>This page updates automatically.</Typography>
        </Box>
        <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}`}</style>
      </Shell>
    );
  }

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // STEP: Menu browsing
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const currentCategoryItems = menu.find((m: any) => m.category?.id === activeCategory)?.items ?? [];

  return (
    <Shell tableInfo={tableInfo}>
      {/* Category tabs */}
      <Box sx={{ display: 'flex', gap: 1, px: 2, py: 1.5, overflowX: 'auto', background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(20px)', borderBottom: `1px solid ${food.glassBorder}`, '&::-webkit-scrollbar': { display: 'none' } }}>
        {menu.map((m: any) => (
          <Chip key={m.category.id} label={m.category.name} onClick={() => setActiveCategory(m.category.id)}
            sx={{ flexShrink: 0, cursor: 'pointer', fontSize: '0.78rem', fontWeight: activeCategory === m.category.id ? 700 : 400,
              background: activeCategory === m.category.id ? food.red : 'transparent',
              color: activeCategory === m.category.id ? '#fff' : food.inkDark,
              border: `1.5px solid ${activeCategory === m.category.id ? food.red : food.glassBorder}` }} />
        ))}
      </Box>

      {/* Items */}
      <Box sx={{ p: 2, pb: 14 }}>
        {currentCategoryItems.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Fastfood sx={{ fontSize: 48, color: food.inkLight, mb: 1 }} />
            <Typography sx={{ color: food.inkLight }}>No items in this category</Typography>
          </Box>
        )}
        {currentCategoryItems.map((item: any) => {
          const qty = getQty(item.id);
          return (
            <Card key={item.id} sx={{ ...glassCard, mb: 1.5 }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', py: '12px !important', px: 2 }}>
                {item.imageUrl && (
                  <Box sx={{ flexShrink: 0, mr: 2, width: 80, height: 80, borderRadius: '12px', overflow: 'hidden' }}>
                    <img src={item.imageUrl.startsWith('http') ? item.imageUrl : `${IMAGE_BASE}${item.imageUrl}`} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </Box>
                )}
                <Box sx={{ flex: 1, pr: 1 }}>
                  <Typography sx={{ color: food.inkDark, fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.3 }}>{item.name}</Typography>
                  {item.description && <Typography variant="caption" sx={{ color: food.inkLight, fontSize: '0.72rem', display: 'block', mt: 0.25 }}>{item.description}</Typography>}
                  <Typography sx={{ color: '#22c55e', fontWeight: 800, mt: 0.5, fontSize: '0.95rem' }}>₹{item.price}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                  {qty > 0 && (
                    <>
                      <IconButton size="small" onClick={() => removeFromCart(item.id)}
                        sx={{ background: food.redSoft, color: food.red, width: 32, height: 32, borderRadius: '10px' }}>
                        <Remove sx={{ fontSize: 16 }} />
                      </IconButton>
                      <Typography sx={{ color: food.inkDark, fontWeight: 800, minWidth: 24, textAlign: 'center', fontSize: '0.95rem' }}>{qty}</Typography>
                    </>
                  )}
                  <IconButton size="small" onClick={() => addToCart(item)}
                    sx={{ background: food.red, color: '#fff', width: 32, height: 32, borderRadius: '10px', '&:hover': { background: food.redDark } }}>
                    <Add sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>

      {/* Sticky cart bar */}
      {cartCount > 0 && (
        <Box sx={{ position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 1000, width: 'calc(100% - 32px)', maxWidth: 448 }}>
          <Button variant="contained" fullWidth onClick={() => setCartOpen(true)}
            sx={{ ...pillBtn, background: food.red, py: 1.5, fontSize: '0.95rem', boxShadow: '0 4px 20px rgba(224,42,42,0.4)', display: 'flex', justifyContent: 'space-between', px: 2.5 }}>
            <Box sx={{ background: 'rgba(255,255,255,0.25)', borderRadius: '8px', px: 1, py: 0.25, fontWeight: 800, fontSize: '0.85rem' }}>{cartCount}</Box>
            <Typography sx={{ fontWeight: 800, fontSize: '0.95rem' }}>View Order</Typography>
            <Typography sx={{ fontWeight: 800 }}>₹{cartTotal.toFixed(0)}</Typography>
          </Button>
        </Box>
      )}

      {/* Cart drawer — mobile-first */}
      <Drawer anchor="bottom" open={cartOpen} onClose={() => setCartOpen(false)}
        PaperProps={{ sx: { borderRadius: '24px 24px 0 0', maxHeight: '85vh', maxWidth: 480, margin: '0 auto', display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}>

        {/* Drag handle */}
        <Box sx={{ pt: 1.5, pb: 0.5, display: 'flex', justifyContent: 'center' }}>
          <Box sx={{ width: 40, height: 4, borderRadius: 2, background: '#e0e0e0' }} />
        </Box>

        {/* Header */}
        <Box sx={{ px: 2, pb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ color: food.inkDark, fontWeight: 800, fontSize: '1.15rem' }}>Your Order</Typography>
          <IconButton size="small" onClick={() => setCartOpen(false)} sx={{ color: food.inkLight, background: '#f5f5f5', borderRadius: '10px' }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>

        {/* Scrollable content */}
        <Box sx={{ overflowY: 'auto', flex: 1, px: 2 }}>
          {/* Cart items */}
          {cart.map(c => (
            <Box key={c.item.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, pb: 1.5, borderBottom: `1px solid #f0f0f0` }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ color: food.inkDark, fontSize: '0.9rem', fontWeight: 600, lineHeight: 1.3 }} noWrap>{c.item.name}</Typography>
                <Typography sx={{ color: '#22c55e', fontWeight: 700, fontSize: '0.85rem' }}>₹{(c.item.price * c.qty).toFixed(0)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                <IconButton size="small" onClick={() => removeFromCart(c.item.id)}
                  sx={{ color: food.red, background: food.redSoft, borderRadius: '8px', width: 30, height: 30 }}>
                  <Remove sx={{ fontSize: 14 }} />
                </IconButton>
                <Typography sx={{ color: food.inkDark, fontWeight: 800, minWidth: 20, textAlign: 'center', fontSize: '0.9rem' }}>{c.qty}</Typography>
                <IconButton size="small" onClick={() => addToCart(c.item)}
                  sx={{ color: '#fff', background: food.red, borderRadius: '8px', width: 30, height: 30 }}>
                  <Add sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            </Box>
          ))}

          {/* Special requests */}
          {cart.map(c => (
            <TextField key={`note-${c.item.id}`} placeholder={`Note for ${c.item.name}`}
              value={c.note} onChange={e => setCart(prev => prev.map(p => p.item.id === c.item.id ? { ...p, note: e.target.value } : p))}
              size="small" fullWidth sx={{ mb: 1, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.8rem' } }} />
          ))}

          <TextField placeholder="Any other notes for the kitchen" fullWidth value={orderNotes}
            onChange={e => setOrderNotes(e.target.value)} size="small" multiline rows={2}
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />

          <Divider sx={{ mb: 2 }} />

          {/* ── Customer details ── */}
          <Typography sx={{ color: food.inkDark, fontWeight: 700, mb: 1, fontSize: '0.95rem' }}>Your Details</Typography>
          <TextField fullWidth label="Your Name" placeholder="e.g. Rahul Sharma" value={customerName}
            onChange={e => { setCustomerName(e.target.value); if (nameError) setNameError(''); }}
            error={!!nameError} helperText={nameError}
            InputProps={{ startAdornment: <InputAdornment position="start"><Person sx={{ color: food.red, fontSize: 18 }} /></InputAdornment> }}
            sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
          <TextField fullWidth label="Mobile Number" placeholder="10-digit number" value={customerPhone}
            onChange={e => { setCustomerPhone(e.target.value.replace(/\D/g, '').slice(0, 10)); if (phoneError) setPhoneError(''); }}
            error={!!phoneError} helperText={phoneError} inputProps={{ inputMode: 'numeric' }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Phone sx={{ color: food.red, fontSize: 18 }} /></InputAdornment> }}
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />

          {/* Total */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
            <Typography sx={{ color: food.inkDark, fontWeight: 600, fontSize: '1rem' }}>Total</Typography>
            <Typography sx={{ color: '#22c55e', fontWeight: 900, fontSize: '1.4rem' }}>₹{cartTotal.toFixed(0)}</Typography>
          </Box>
        </Box>

        {/* Confirm button — pinned at bottom */}
        <Box sx={{ px: 2, pb: 3, pt: 1.5, background: '#fff', borderTop: `1px solid #f0f0f0` }}>
          <Button variant="contained" fullWidth disabled={placing}
            startIcon={placing ? <CircularProgress size={18} color="inherit" /> : <Send />}
            onClick={proceedToPayment}
            sx={{ ...pillBtn, background: food.red, py: 1.6, fontSize: '1rem', boxShadow: '0 4px 16px rgba(224,42,42,0.3)' }}>
            {placing ? 'Confirming…' : 'Proceed to Payment →'}
          </Button>
        </Box>
      </Drawer>

      {/* ── Maya Chatbot FAB ── */}
      <IconButton
        onClick={() => {
            setChatOpen(true);
            if (chatMessages.length === 0) {
                setChatMessages([{ sender: 'bot', text: "👋 Hi! I'm Maya, your personal AI Waiter for this restaurant!\n\n🍽️ I can help you find the perfect dish. What are you in the mood for? 😊" }]);
            }
        }}
        sx={{
          position: 'fixed', bottom: cartCount > 0 ? 80 : 20, right: 20, zIndex: 1000,
          background: food.inkDark, color: '#fff', width: 56, height: 56, boxShadow: shadows.glow,
          '&:hover': { background: '#000' }
        }}
      >
        <span style={{ fontSize: 24 }}>👩‍🍳</span>
      </IconButton>

      {/* ── Maya Chat Drawer ── */}
      <Drawer anchor="right" open={chatOpen} onClose={() => setChatOpen(false)}
        PaperProps={{ sx: { width: '100%', maxWidth: 480, height: '100%', display: 'flex', flexDirection: 'column' } }}>
        <Box sx={{ background: food.inkDark, color: '#fff', px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ fontSize: 32, background: 'rgba(255,255,255,0.1)', borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>👩‍🍳</Box>
                <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>Maya</Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)' }}>Your AI Waiter</Typography>
                </Box>
            </Box>
            <IconButton onClick={() => setChatOpen(false)} sx={{ color: '#fff' }}><Close /></IconButton>
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 2, background: food.bg }}>
            {chatMessages.map((msg, i) => (
                <Box key={i} sx={{ alignSelf: msg.sender === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
                    <Box sx={{
                        background: msg.sender === 'user' ? food.red : '#fff',
                        color: msg.sender === 'user' ? '#fff' : food.inkDark,
                        p: 1.5, borderRadius: msg.sender === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)', fontSize: '0.9rem', whiteSpace: 'pre-wrap'
                    }}>
                        {msg.text}
                    </Box>
                    {msg.items && msg.items.length > 0 && (
                        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {msg.items.map(mItem => {
                                const fullItem = menu.flatMap(c => c.items).find((x: any) => x.id === mItem.id);
                                return (
                                <Box key={mItem.id} sx={{ background: '#fff', p: 1.5, borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: `1px solid ${food.glassBorder}` }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        {fullItem && fullItem.imageUrl && (
                                            <img src={fullItem.imageUrl.startsWith('http') ? fullItem.imageUrl : `${IMAGE_BASE}${fullItem.imageUrl}`} alt={fullItem.name} style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover' }} />
                                        )}
                                        <Box>
                                            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>{mItem.name}</Typography>
                                            <Typography sx={{ color: '#22c55e', fontWeight: 800, fontSize: '0.85rem' }}>₹{mItem.price}</Typography>
                                        </Box>
                                    </Box>
                                    {fullItem && (
                                        <Button variant="contained" size="small"
                                            onClick={() => { addToCart(fullItem); setChatOpen(false); setCartOpen(true); }}
                                            sx={{ ...pillBtn, background: food.red, minWidth: 0, px: 1.5, py: 0.5, fontSize: '0.75rem' }}>
                                            Add
                                        </Button>
                                    )}
                                </Box>
                            )})}
                        </Box>
                    )}
                </Box>
            ))}
            {chatLoading && (
                <Box sx={{ alignSelf: 'flex-start', background: '#fff', p: 1.5, borderRadius: '16px 16px 16px 4px', display: 'flex', gap: 1 }}>
                    <CircularProgress size={16} sx={{ color: food.red }} />
                    <Typography sx={{ fontSize: '0.85rem', color: food.inkLight }}>Maya is typing...</Typography>
                </Box>
            )}
        </Box>

        <Box sx={{ p: 2, background: '#fff', borderTop: `1px solid ${food.glassBorder}` }}>
            <TextField
                fullWidth placeholder="Ask Maya anything..." value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSendChat(); }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton onClick={handleSendChat} disabled={!chatInput.trim() || chatLoading} sx={{ color: food.red }}>
                                <Send />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '24px', background: food.bg } }}
            />
        </Box>
      </Drawer>
    </Shell>
  );
};

export default CustomerOrderPage;
